/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { Query } from 'react-apollo';
import gql from 'graphql-tag';
import mbpLogger from 'mbp-logger';
import { object, string } from 'prop-types';
import { compose } from 'recompose';
import Media from 'react-media';

import * as app from '../../../../state/ducks/App';
import { GRAPHQL_ENV } from '../../../gql';
import HomePageSkeleton from '../GraphqlSkeletonComponents/HomePageSkeleton';
import GraphqlDepartmentPageBody from './GraphqlDepartmentPageBody';
import GraphqlBreadcrumbs from '../GraphqlCommonComponents/GraphqlBreadcrumbs/GraphqlBreadcrumbs';
import { coreBrowsingPageViewSuccessTest } from '../../../helpers/tracking/common/commonTrackingHelpers';
import withPageView from '../../../helpers/tracking/hocs/withPageView';
import GraphqlSeoHeadTags from '../GraphqlCommonComponents/GraphqlSeoHeadTags/GraphqlSeoHeadTags';
import Graphql404ErrorContainer from '../Graphql404Error/Graphql404ErrorContainer';

const appSelectors = app.selectors;
const brandSelectors = app.ducks.brand.selectors;

const GraphqlDepartmentPage = ({ brand, page, ssrDeviceType }) => {
    const DEPARTMENTPAGE_QUERY = gql`
            query DepartmentPageQuery {
                findContentPage(brand: "${brand['domain-name']}", url: "${page?.path}", contentType: "department", environment: "${GRAPHQL_ENV}", locale: "en-us") {
                    content
                }
            }
        `;

    return (
        <Query query={DEPARTMENTPAGE_QUERY}>
            {({ loading, error, data }) => {
                if (loading) {
                    return <HomePageSkeleton />; // skeleton component
                }

                if (error) {
                    mbpLogger.logError({
                        query: DEPARTMENTPAGE_QUERY,
                        component: 'GraphqlDepartmentPage.js',
                        message: 'Error loading data from Graphql',
                        env: GRAPHQL_ENV,
                        error,
                    });

                    return null;
                }

                if (!data) {
                    mbpLogger.logWarning({
                        query: DEPARTMENTPAGE_QUERY,
                        component: 'GraphqlDepartmentPage.js',
                        message: 'No data returned for query',
                        env: GRAPHQL_ENV,
                    });

                    return <Graphql404ErrorContainer brand={brand} page={page} />;
                }
                if (!data.findContentPage?.content) {
                    mbpLogger.logWarning({
                        query: DEPARTMENTPAGE_QUERY,
                        data,
                        component: 'GraphqlDepartmentPage.js',
                        message: 'No content returned for query',
                    });
                    return <Graphql404ErrorContainer brand={brand} page={page} />;
                }

                const pageBlocks = data.findContentPage.content.entries?.[0]?.page_blocks || null;
                if (!pageBlocks) {
                    mbpLogger.logWarning({
                        query: DEPARTMENTPAGE_QUERY,
                        data,
                        component: 'GraphqlDepartmentPage.js',
                        message: 'No page blocks for query',
                    });
                    return <Graphql404ErrorContainer brand={brand} page={page} />;
                }

                const untData = data.findContentPage.underNavText?.entries || null;
                const SEO = data.findContentPage.content.entries?.[0]?.seo || null;
                const domainURL = `https://pwa.${brand.domain}.com` || '';
                return (
                    <>
                        <Media query="(min-width: 600px)">
                            {SEO?.breadcrumb?.length
                            && (
                                <div style={{
                                    display: 'block', maxWidth: '1400px', margin: '0 auto', padding: '20px 0 0 20px',
                                }}
                                >
                                    <GraphqlBreadcrumbs breadCrumbArray={SEO.breadcrumb} />
                                </div>
                            )}
                        </Media>
                        <GraphqlSeoHeadTags seoData={SEO} />
                        <GraphqlDepartmentPageBody
                            brand={brand}
                            page={page}
                            blocks={pageBlocks}
                            untData={untData}
                            seo={SEO}
                            domainURL={domainURL}
                            ssrDeviceType={ssrDeviceType}
                        />
                        <Media query="(max-width: 599px)">
                            {SEO?.breadcrumb?.length
                            && (
                                <div style={{ padding: '20px 0 0 20px' }}>
                                    <GraphqlBreadcrumbs breadCrumbArray={SEO.breadcrumb} />
                                </div>
                            )}
                        </Media>
                    </>
                );
            }}
        </Query>
    );
};

GraphqlDepartmentPage.propTypes = {
    brand: object.isRequired,
    page: object.isRequired,
    ssrDeviceType: string.isRequired,
};

const mapStateToProps = (state) => ({
    brand: brandSelectors.getBrand(state),
    ssrDeviceType: appSelectors.getSSRDeviceType(state),
});

const enhance = compose(
    withRouter,
    connect(mapStateToProps),
    withPageView({
        pageViewSuccessTest: coreBrowsingPageViewSuccessTest,
    }),
);

export default enhance(GraphqlDepartmentPage);
